import { parseDateValue } from '../utils/formatUtils';
import React, { useState, useEffect, useCallback } from "react";
import "./ImageFilter.css";
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import { getAPIUrl } from "../api/apiUtils";
import { getGroupTitleById } from "../utils/groupTitles";
import { formatRuDateTime } from "../utils/ruDateUtils";
import WordCloud from "./WordCloud";
import JsonModal from "./DetailsModal";
import WordDetailsModal from "./WordDetailsModal";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IconButton } from "@mui/material";
import { useGlobalFilters, useGlobalFiltersSubscription } from '../hooks/useGlobalFilters';
import Pagination from "../components/Pagination";
import { apiFetch } from "../api/apiClient";
import { PAGE_KEYS } from "../utils/pageAccess";


const API_URL = getAPIUrl();

const ImageFilter = ({ allowedPages = [] }) => {
  const filtersStore = useGlobalFilters();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('table'); // 'table' или 'wordcloud'
  const canViewTable = allowedPages.includes(PAGE_KEYS.EMOTIVE_REASONS_DATA);
  const canViewWordCloud = allowedPages.includes(PAGE_KEYS.EMOTIVE_REASONS_WORDS);
  const allowedReasonPagesCount = Number(canViewTable) + Number(canViewWordCloud);
  const effectiveActiveTab =
    (activeTab === 'table' && canViewTable) || (activeTab === 'wordcloud' && canViewWordCloud)
      ? activeTab
      : canViewTable
        ? 'table'
        : canViewWordCloud
          ? 'wordcloud'
          : null;
  
  // Состояние для модального окна с JSON
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonData, setJsonData] = useState(null);
  
  // Состояние для модального окна с деталями слова
  const [showWordDetailsModal, setShowWordDetailsModal] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);
  
  // Состояние для сортировки
  const [sorting, setSorting] = useState([]);
  
  // Состояние для пагинации
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  // Используем современный подход с MobX реактивностью
  const [globalFilters, setGlobalFilters] = useState(filtersStore.filters);
  
  // Подписываемся на изменения фильтров через MobX
  const handleFiltersChange = useCallback((filters) => {
    setGlobalFilters(filters);
    // Сбрасываем пагинацию при изменении фильтров
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Обработчик клика по слову в облаке слов
  const handleWordClick = useCallback((word, frequency) => {
    setSelectedWord(word);
    setShowWordDetailsModal(true);
  }, []);

  const PREDICATE_GROUPS_KEY = 'selectedPredicateGroups_wordcloud';

  const [selectedGroups, setSelectedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(PREDICATE_GROUPS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Ошибка загрузки selectedGroups из localStorage:', error);
      return [];
    }
  });

  useGlobalFiltersSubscription(handleFiltersChange);

  // Загружаем данные при изменении фильтров, пагинации или сортировки
  useEffect(() => {
    fetchData(pagination.pageIndex, pagination.pageSize, sorting);
  }, [globalFilters, pagination.pageIndex, pagination.pageSize, sorting]);

  // Загружаем все данные для облака слов при изменении фильтров
  useEffect(() => {
    fetchAllDataForWordCloud();
  }, [globalFilters]);

  // Загрузка всех данных для облака слов (без пагинации)
  const fetchAllDataForWordCloud = async () => {
    try {
      const params = new URLSearchParams({
        causators_only: true,
        limit: 1000, // Большой лимит для загрузки всех данных
      });

      // Добавляем фильтры по датам
      if (globalFilters.startDate) {
        params.append('start_date', globalFilters.startDate);
      }
      if (globalFilters.endDate) {
        params.append('end_date', globalFilters.endDate);
      }

      // Добавляем фильтр по группам
      if (globalFilters.selectedGroups && globalFilters.selectedGroups.length > 0) {
        params.append('group_ids', globalFilters.selectedGroups.join(','));
      }

      const url = `${API_URL}/detection/srl_analysis?${params.toString()}`;
      console.log('Запрос всех данных для облака слов:', url);

      let response = await apiFetch(url);
      
      // Если запрос с фильтрами не удался, пробуем без фильтров
      if (!response.ok && (globalFilters.selectedGroups.length > 0 || globalFilters.startDate || globalFilters.endDate)) {
        console.warn('API не поддерживает фильтры для облака слов, загружаем все данные');
        response = await apiFetch(`${API_URL}/detection/srl_analysis?causators_only=true&limit=1000`);
      }
      
      const result = await response.json();
      console.log('Все данные для облака слов:', result.data?.length || 0);

      if (!response.ok) {
        console.error('Не удалось загрузить данные для облака слов');
        return;
      }

      // Обрабатываем все данные для облака слов
      if (result.data && Array.isArray(result.data)) {
        // Фильтруем данные на клиенте (удаляем пустые full_analysis и causators)
        const filtered = result.data.filter((item) => {
          const isFullAnalysisEmpty =
            !item.full_analysis ||
            (Array.isArray(item.full_analysis) && item.full_analysis.length === 0) ||
            (typeof item.full_analysis === 'object' && Object.keys(item.full_analysis).length === 0);
          const isCausatorsEmpty = !item.causators || item.causators === null;

          return !(isFullAnalysisEmpty && isCausatorsEmpty);
        });

        // Дополнительная фильтрация на клиенте, если API не поддерживает фильтры
        let finalFiltered = filtered;
        
        // Фильтрация по группам на клиенте (если API не поддерживает)
        if (globalFilters.selectedGroups && globalFilters.selectedGroups.length > 0) {
          finalFiltered = finalFiltered.filter(item => 
            globalFilters.selectedGroups.includes(String(item.group_id))
          );
        }

        // Фильтрация по датам на клиенте (если API не поддерживает)
        if (globalFilters.startDate || globalFilters.endDate) {
          finalFiltered = finalFiltered.filter(item => {
            const itemDate = parseDateValue(item.date);
            if (!itemDate) return false;
            const startDate = globalFilters.startDate ? new Date(globalFilters.startDate) : null;
            const endDate = globalFilters.endDate ? new Date(globalFilters.endDate) : null;
            
            if (startDate && itemDate < startDate) return false;
            if (endDate && itemDate > endDate) return false;
            return true;
          });
        }

        console.log('Всего данных для облака слов:', finalFiltered.length);
        setData(finalFiltered); // Обновляем данные для облака слов
      }
    } catch (err) {
      console.error('Ошибка загрузки данных для облака слов:', err);
    }
  };

  // Загрузка данных с фильтрами и пагинацией
  const fetchData = async (pageIndex = 0, pageSize = 10, sortConfig = []) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: pageIndex + 1, // API ожидает 1-based индексацию
        limit: pageSize,
        causators_only: true,
      });

      // Добавляем фильтры по датам
      if (globalFilters.startDate) {
        params.append('start_date', globalFilters.startDate);
      }
      if (globalFilters.endDate) {
        params.append('end_date', globalFilters.endDate);
      }

      // Добавляем фильтр по группам
      if (globalFilters.selectedGroups && globalFilters.selectedGroups.length > 0) {
        params.append('group_ids', globalFilters.selectedGroups.join(','));
      }

      // Добавляем сортировку
      if (sortConfig.length > 0) {
        const sort = sortConfig[0];
        params.append('sort_by', sort.id);
        params.append('sort_order', sort.desc ? 'desc' : 'asc');
      }

      const url = `${API_URL}/detection/srl_analysis?${params.toString()}`;
      console.log('Запрос к API:', url);

      let response = await apiFetch(url);
      
      // Если запрос с фильтрами не удался, пробуем без фильтров
      if (!response.ok && (globalFilters.selectedGroups.length > 0 || globalFilters.startDate || globalFilters.endDate)) {
        console.warn('API не поддерживает фильтры, загружаем все данные');
        const fallbackParams = new URLSearchParams({
          page: pageIndex + 1,
          limit: pageSize,
        });
        response = await apiFetch(`${API_URL}/detection/srl_analysis?${fallbackParams.toString()}`);
      }
      
      const result = await response.json();
      console.log('Ответ от API:', result);

      if (!response.ok) {
        throw new Error('Не удалось загрузить данные');
      }

      // Проверяем структуру ответа
      if (!result.data || !Array.isArray(result.data)) {
        console.error('Неверная структура ответа:', result);
        throw new Error('Неверная структура данных от сервера');
      }

      console.log('Количество записей от сервера:', result.data.length);

      // Фильтруем данные на клиенте (удаляем пустые full_analysis и causators)
      const filtered = result.data.filter((item) => {
        const isFullAnalysisEmpty =
          !item.full_analysis ||
          (Array.isArray(item.full_analysis) && item.full_analysis.length === 0) ||
          (typeof item.full_analysis === 'object' && Object.keys(item.full_analysis).length === 0);
        const isCausatorsEmpty = !item.causators || item.causators === null;

        return !(isFullAnalysisEmpty && isCausatorsEmpty);
      });

      console.log('Количество записей после фильтрации пустых:', filtered.length);

      // Дополнительная фильтрация на клиенте, если API не поддерживает фильтры
      let finalFiltered = filtered;
      
      // Фильтрация по группам на клиенте (если API не поддерживает)
      if (globalFilters.selectedGroups && globalFilters.selectedGroups.length > 0) {
        finalFiltered = finalFiltered.filter(item => 
          globalFilters.selectedGroups.includes(String(item.group_id))
        );
        console.log('Количество записей после фильтрации по группам:', finalFiltered.length);
      }

      // Фильтрация по датам на клиенте (если API не поддерживает)
      if (globalFilters.startDate || globalFilters.endDate) {
        finalFiltered = finalFiltered.filter(item => {
          const itemDate = parseDateValue(item.date);
          if (!itemDate) return false;
          const startDate = globalFilters.startDate ? new Date(globalFilters.startDate) : null;
          const endDate = globalFilters.endDate ? new Date(globalFilters.endDate) : null;
          
          if (startDate && itemDate < startDate) return false;
          if (endDate && itemDate > endDate) return false;
          return true;
        });
        console.log('Количество записей после фильтрации по датам:', finalFiltered.length);
      }

      console.log('Финальное количество записей после фильтров (текущая страница):', finalFiltered.length);
      const total = typeof result.total === 'number' ? result.total : finalFiltered.length;
      setData(result.data);
      setFilteredData(finalFiltered);
      setTotalRows(total);
      setPageCount(Math.ceil(total / pageSize));
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError('Не удалось загрузить данные');
      setData([]);
      setFilteredData([]);
      setTotalRows(0);
      setPageCount(1);
    } finally {
      setLoading(false);
    }
  };

  // Функция для открытия модального окна с JSON
  const openJsonModal = (data) => {
    setJsonData(data);
    setShowJsonModal(true);
  };

  // Функция для закрытия модального окна
  const closeJsonModal = () => {
    setShowJsonModal(false);
    setJsonData(null);
  };

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ row }) => row.original.id,
    },
    {
      accessorKey: 'causators',
      header: 'Причина эмоции',
      cell: ({ row }) => {
        const val = row.original.causators;
        if (!val) return 'Нет данных';
        try {
          // Если это строка, парсим
          const obj = typeof val === 'string' ? JSON.parse(val) : val;
          // Красивый вывод: ключ: значение, ключ: значение
          return Object.entries(obj)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ');
        } catch (e) {
          // Если не парсится — выводим как есть
          return String(val);
        }
      }
    },
    {
      accessorKey: 'group_id',
      header: 'Группа',
      cell: ({ row }) => {
        const gid = row.original.group_id;
        const title = getGroupTitleById(gid);
        return `${title} (ID: ${gid})`;
      }
    },
    {
      accessorKey: 'post_id',
      header: 'ID поста',
      cell: ({ row }) => {
        const gid = row.original.group_id;
        const pid = row.original.post_id;
        if (gid && pid) {
          return (
            <a href={`https://vk.com/wall${gid}_${pid}`} target="_blank" rel="noopener noreferrer">{pid}</a>
          );
        }
        return pid;
      },
    },
    {
      accessorKey: 'comment_id',
      header: 'ID комментария',
      cell: ({ row }) => {
        const gid = row.original.group_id;
        const pid = row.original.post_id;
        const cid = row.original.comment_id;
        // Если есть комментарий, создаем ссылку на него
        if (gid && pid && cid) {
          return (
            <a href={`https://vk.com/wall${gid}_${pid}?reply=${cid}`} target="_blank" rel="noopener noreferrer">{cid}</a>
          );
        }
        return cid || '—';
      },
    },
    {
      accessorKey: 'date',
      header: 'Дата',
      cell: ({ row }) => formatRuDateTime(parseDateValue(row.original.date)) || 'Нет даты',
    },
    {
      accessorKey: 'type',
      header: 'Тип',
      cell: ({ row }) => row.original.type,
    },
    {
      accessorKey: 'full_analysis',
      header: 'Детали',
      cell: ({ row }) => {
        const analysis = row.original.full_analysis;
        if (!analysis || (Array.isArray(analysis) && analysis.length === 0) || 
            (typeof analysis === 'object' && Object.keys(analysis).length === 0)) {
          return 'Нет данных';
        }
        return (
          <IconButton color="primary"
            onClick={() => openJsonModal(analysis)}            
          >
            <VisibilityOutlinedIcon />
          </IconButton>
        );
      },
    },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Управляем пагинацией вручную
    manualPagination: true,
    manualSorting: true,
    pageCount: pageCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
    },
  });

  return (
    <div className="emo-page-container">
      <div className="max-w-7xl mx-auto">
        
        {/* Вкладки */}
        {allowedReasonPagesCount > 1 && (
        <div className="tabs-container mb-4">
          <div className="tabs">
            {canViewTable && (
              <button
                className={`tab ${effectiveActiveTab === 'table' ? 'active' : ''}`}
                onClick={() => setActiveTab('table')}
              >
                Данные анализа
              </button>
            )}
            {canViewWordCloud && (
              <button
                className={`tab ${effectiveActiveTab === 'wordcloud' ? 'active' : ''}`}
                onClick={() => setActiveTab('wordcloud')}
              >
                Причины эмоций
              </button>
            )}
          </div>
        </div>
        )}

        <div className="emo-table-scroll-container">
          {globalFilters.selectedGroups.length === 0 ? (
            <div className="text-center p-8">
              <p className="emo-no-data text-gray-500">Нет выбранной группы</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="emo-loading">Загрузка данных...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center p-8">
              <p className="emo-error text-red-600">{error}</p>
              <button 
                onClick={() => fetchData(pagination.pageIndex, pagination.pageSize, sorting)}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Попробовать снова
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center p-8">
              <p className="emo-no-data text-gray-500">
                {globalFilters.selectedGroups.length > 0 || globalFilters.startDate || globalFilters.endDate
                  ? "Нет данных, соответствующих выбранным фильтрам"
                  : "Нет данных для отображения"
                }
              </p>
            </div>
          ) : (
            <>
            {allowedReasonPagesCount > 0 ? (
              <>
              {/* Вкладка "Таблица данных" */}
              {effectiveActiveTab === 'table' && canViewTable && (
                <>
                  <div className="emo-table-wrapper">
                    <table className="emo-data-table">
                      <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <tr key={headerGroup.id} className="bg-gray-200">
                            {headerGroup.headers.map((header) => (
                              <th
                                key={header.id}
                                onClick={header.column.getToggleSortingHandler()}
                                className={`border p-2 text-left cursor-pointer ${
                                  header.column.getIsSorted() ? 'emo-sorted bg-gray-300' : ''
                                }`}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getIsSorted() === 'asc'
                                  ? ' 🔼'
                                  : header.column.getIsSorted() === 'desc'
                                  ? ' 🔽'
                                  : ''}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {table.getRowModel().rows.map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id} className="border p-2">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ fontWeight: 600, color: "#1976d2", fontSize: 16 }}>
                          
                        </span>
                        <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 14 }}>
                          Показано {totalRows > 0 ? ((table.getState().pagination.pageIndex * table.getState().pagination.pageSize) + 1) : 0}-{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, totalRows)} из {totalRows} записей
                        </span>
                      </div>
                      <select 
                        value={table.getState().pagination.pageSize} 
                        onChange={(e) => table.setPageSize(Number(e.target.value))}
                        style={{
                          padding: "8px 12px",
                          borderRadius: 8,
                          border: "1px solid #4a90e2",
                          background: "#fff",
                          fontSize: 14,
                          color: "#374151",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          outline: "none"
                        }}
                        onFocus={(e) => e.target.style.borderColor = "#357abd"}
                        onBlur={(e) => e.target.style.borderColor = "#4a90e2"}
                      >
                        {[10, 20, 30, 50].map((size) => (
                          <option key={size} value={size}>Показать {size}</option>
                        ))}
                      </select>
                    </div>
                    <Pagination
                      currentPage={table.getState().pagination.pageIndex + 1}
                      totalPages={table.getPageCount()}
                      onPageChange={(newPage) => table.setPageIndex(newPage - 1)}
                      maxVisiblePages={5}
                    />
                  </div>
                </>
              )}

              {/* Вкладка "Каузаторы" */}
              {effectiveActiveTab === 'wordcloud' && canViewWordCloud && (
                <WordCloud data={data} onWordClick={handleWordClick} selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups} />
              )}
              </>
            ) : (
                <div className="text-center p-8">
                  <p className="emo-no-data text-gray-500">У вас нет доступа к этой странице</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Модальное окно для отображения JSON */}
      <JsonModal
        isOpen={showJsonModal}
        onClose={closeJsonModal}
        data={jsonData}
        title="Полный анализ (JSON)"
      />

      {/* Модальное окно с деталями слова */}
      <WordDetailsModal
        isOpen={showWordDetailsModal}
        onClose={() => setShowWordDetailsModal(false)}
        selectedWord={selectedWord}
        data={data}
        selectedGroups={selectedGroups}
      />
    </div>
  );
};

export default ImageFilter;
